import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Pause, Play, Sparkles, Wrench, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/changelog`;

const categoryIcons = {
  feature: Sparkles,
  fix: Wrench,
  improvement: Zap,
};
const categoryColors = {
  feature: 'text-sky-800 bg-sky-100 border-sky-200',
  fix: 'text-amber-800 bg-amber-100 border-amber-200',
  improvement: 'text-emerald-800 bg-emerald-100 border-emerald-200',
};

export default function ChangelogSplash({ onDismiss }) {
  const [data, setData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(15);
  const [paused, setPaused] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const timerRef = useRef(null);
  const dismissed = useRef(false);

  useEffect(() => {
    axios.get(`${API}/should-show`).then(r => {
      if (r.data.show) {
        setData(r.data);
        setTimeLeft(r.data.duration || 15);
      } else {
        onDismiss();
      }
    }).catch(() => onDismiss());
  }, [onDismiss]);

  useEffect(() => {
    if (!data || paused) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (!dismissed.current) { dismissed.current = true; handleDismiss(); }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [data, paused]);

  const handleDismiss = () => {
    clearInterval(timerRef.current);
    axios.post(`${API}/dismiss`, { dont_show_again: dontShowAgain }).catch(() => {});
    onDismiss();
  };

  if (!data) return null;

  const entries = data.entries || [];
  const progress = data.duration ? ((data.duration - timeLeft) / data.duration) * 100 : 0;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl ti-panel rounded-2xl shadow-2xl overflow-hidden">
        {/* Timer bar */}
        <div className="h-1 bg-slate-200/70">
          <div className="h-full bg-gradient-to-r from-sky-500 to-blue-800 transition-all duration-1000 ease-linear" style={{ width: `${progress}%` }} />
        </div>

        {/* Header */}
        <div className="px-6 pt-5 pb-3 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-black text-slate-900 tracking-tight">What's New in 365Ops</h2>
            <p className="text-xs text-slate-500 mt-0.5">Latest updates and improvements</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPaused(!paused)}
              className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-900 transition-colors shadow-sm"
              title={paused ? 'Resume timer' : 'Pause timer'}
            >
              {paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
            </button>
            <span className="text-xs text-slate-500 font-mono w-6 text-center">{timeLeft}s</span>
            <button
              onClick={handleDismiss}
              className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-900 transition-colors shadow-sm"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Entries */}
        <div className="px-6 pb-5 max-h-[60vh] overflow-y-auto space-y-4">
          {entries.map(entry => {
            const Icon = categoryIcons[entry.category] || Sparkles;
            const colorClass = categoryColors[entry.category] || categoryColors.feature;
            return (
              <div key={entry.id} className="rounded-xl border border-slate-200/90 bg-white/80 overflow-hidden">
                <div className="flex items-center gap-2.5 px-4 py-2.5 bg-slate-50/90 border-b border-slate-100">
                  <div className={`p-1.5 rounded-lg border ${colorClass}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 flex-1">{entry.title}</h3>
                  <span className="text-[10px] text-slate-500 font-mono">{entry.date}</span>
                </div>
                <div className="px-4 py-2.5">
                  <ul className="space-y-1">
                    {(entry.items || []).map((item, i) => (
                      <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                        <span className="text-sky-600 mt-0.5 flex-shrink-0">-</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200/80 flex items-center justify-between bg-slate-50/60">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={e => setDontShowAgain(e.target.checked)}
              className="rounded border-slate-300 text-sky-600 w-4 h-4"
            />
            <span className="text-xs text-slate-500">Don't show this again</span>
          </label>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-400">
              {paused ? 'Paused' : `${timeLeft}s`}
            </span>
            <button
              onClick={handleDismiss}
              className="px-5 py-2 bg-slate-900 hover:bg-sky-800 text-white rounded-lg text-sm font-bold transition-colors shadow-sm"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
